import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';
import { Configuration, LeatherTypes, SeatBackStyles, SeatPleatingStyles, TunnelSidesOptions, EmbroideryOptions } from '../data/configuration.js';
import { LeatherDescription } from './components/leather-description.js';
import { CarpetDescription } from './components/carpet-description.js';
import { DashFaceDescription } from './components/dash-face-description.js';
import { ColourDescription } from './components/colour-description.js';
import { PaintDescription } from './components/paint-description.js';
import { CentreConsoleDescription } from './components/centre-console-description.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';
import { CarPbrMaterial, cloneBodyPaintMaterial } from './paint-configurator.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SeatMaterial extends Material {
	seatColor: NumberArray;
	pipeColor: NumberArray;
	stitchingColor: NumberArray;
}

interface SeatTextileMaterial extends Material {
	albedoColor: NumberArray;
}

export interface CarpetMaterial extends Material {
	albedoColor: NumberArray;
}

type FadableMaterial = Material | CarpetMaterial | SeatMaterial;
type FadableKey = {
	[TK in keyof FadableMaterial]: FadableMaterial[TK] extends NumberArray ? TK : never;
}[keyof FadableMaterial];

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;
const GLOSS_SMOOTHNESS_VALUE = 0.9;
const GLOSS_REFLECTIVE_SPECULAR_VALUE = 0.96;
const MATT_SMOOTHNESS_VALUE = 0.4;
const MATT_REFLECTIVE_SPECULAR_VALUE = 0.4;

/**
 * Interior Configurator:
 */
export class InteriorConfigurator extends Component {
	static TypeName = 'interior-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	gearStickManual!: Object3D;

	@property.object({ required: true })
	gearStickManualLeather!: Object3D;

	@property.object({ required: true })
	gearStickAuto!: Object3D;

	@property.object({ required: true })
	gearStickAutoLeather!: Object3D;

	@property.object({ required: true })
	leatherDescriptions!: Object3D;

	@property.object({ required: true })
	seatStitchingDescriptions!: Object3D;

	@property.object({ required: true })
	tunnelCarpet!: Object3D;

	@property.object({ required: true })
	tunnelLeather!: Object3D;

	@property.object({ required: true })
	interiorLeatherNoAudio!: Object3D;

	@property.object({ required: true })
	interiorLeatherAudio!: Object3D;

	@property.object({ required: true })
	doorCardNoAudio!: Object3D;

	@property.object({ required: true })
	doorCardAudio!: Object3D;

	@property.object({ required: true })
	doorCardBase!: Object3D;

	@property.object({ required: true })
	gloveBox!: Object3D;

	@property.object({ required: true })
	tunnelPocket!: Object3D;

	@property.object({ required: true })
	lockableStorage!: Object3D;
	
	@property.object({ required: true })
	interiorSideScreen!: Object3D;

	@property.object({ required: true })
	seatBase!: Object3D;

	@property.object({ required: true })
	seatBoxStitch!: Object3D;

	@property.object({ required: true })
	seatDiamondStitch!: Object3D;

	@property.object({ required: true })
	seatHorizontalStitch!: Object3D;

	@property.object({ required: true })
	seatBackLeather!: Object3D;

	@property.object({ required: true })
	seatBackTextile!: Object3D;

	@property.object({ required: true })
	visors!: Object3D;

	@property.object({ required: true })
	carpetDescriptions!: Object3D;

	@property.object({ required: true })
	carpets!: Object3D;

	@property.object({ required: true })
	seatHeadrestStitch!: Object3D;

	@property.object({ required: true })
	seatHeadrestCustomStitch!: Object3D;

	@property.object({ required: true })
	dashFaceDescriptions!: Object3D;

	@property.object({ required: true })
	dashFace!: Object3D;

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	steeringWheelStandard!: Object3D;

	@property.object({ required: true })
	steeringWheelMidsummer!: Object3D;

	@property.object({ required: true })
	interiorComponents!: Object3D;

	@property.object({ required: true })
	centreConsoleDescriptions!: Object3D;

	@property.object({ required: true })
	centreConsoleWood!: Object3D;

	@property.object({ required: true })
	centreConsoleLeather!: Object3D;

	@property.object({ required: true })
	centreConsoleCarPaint!: Object3D;

	@property.object({ required: true })
	centreConsoleMarquetry!: Object3D;

	@property.object({ required: true })
	dashLowerRail!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _gearStickManual!: MeshComponent;
	private _gearStickManualLeather!: MeshComponent;
	private _gearStickAuto!: MeshComponent;
	private _gearStickAutoLeather!: MeshComponent;
	private _leatherDescriptions!: LeatherDescription[];
	private _seatStitchingDescriptions!: ColourDescription[];
	private _tunnelLeather!: MeshComponent;
	private _tunnelCarpet!: MeshComponent;
	private _interiorLeatherNoAudio!: MeshComponent;
	private _interiorLeatherAudio!: MeshComponent;
	private _doorCardNoAudio!: MeshComponent;
	private _doorCardAudio!: MeshComponent;
	private _doorCardBase!: MeshComponent;
	private _gloveBox!: MeshComponent;
	private _tunnelPocket!: MeshComponent;
	private _lockableStorage!: MeshComponent;
	private _interiorSideScreen!: MeshComponent;
	private _seatBase!: MeshComponent;
	private _seatBoxStitch!: MeshComponent;
	private _seatDiamondStitch!: MeshComponent;
	private _seatHorizontalStitch!: MeshComponent;
	private _seatBackLeather!: MeshComponent;
	private _seatBackTextile!: MeshComponent;
	private _visors!: MeshComponent[];
	private _carpetDescriptions!: CarpetDescription[];
	private _carpets!: MeshComponent;
	private _seatHeadrestStitch!: MeshComponent;
	private _seatHeadrestCustomStitch!: MeshComponent;
	private _dashFaceDesciptions!: DashFaceDescription[];
	private _dashFace!: MeshComponent;
	private _paintDescriptions!: PaintDescription[];
	private _interiorComponents!: MeshComponent;
	private _centreConsoleDescriptions!: CentreConsoleDescription[];
	private _centreConsoleWood!: MeshComponent;
	private _centreConsoleLeather!: MeshComponent;
	private _centreConsoleCarPaint!: MeshComponent;
	private _centreConsoleMarquetry!: MeshComponent;
	private _dashLowerRail!: MeshComponent;

	private _lastLeatherType: LeatherTypes | null = null;
	// the dash face material while a body-matching dashboard option is selected, kept so that a changing body colour can be written
	// straight onto it rather than rebuilding it. null whenever the selected dashboard option does not follow the body colour.
	private _bodyMatchingDashboardMaterial: CarPbrMaterial | null = null;

	private _tweenManager = new TweenManager();

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start(): void {

		// get components.
		this._gearStickManual = this.gearStickManual.getComponent(MeshComponent)!;
		this._gearStickManualLeather = this.gearStickManualLeather.getComponent(MeshComponent)!;
		this._gearStickAuto = this.gearStickAuto.getComponent(MeshComponent)!;
		this._gearStickAutoLeather = this.gearStickAutoLeather.getComponent(MeshComponent)!;
		this._leatherDescriptions = this.leatherDescriptions.getComponents(LeatherDescription)!;
		this._seatStitchingDescriptions = this.seatStitchingDescriptions.getComponents(ColourDescription)!;
		this._tunnelLeather = this.tunnelLeather.getComponent(MeshComponent)!;
		this._tunnelCarpet = this.tunnelCarpet.getComponent(MeshComponent)!;
		this._interiorLeatherNoAudio = this.interiorLeatherNoAudio.getComponent(MeshComponent)!;
		this._interiorLeatherAudio = this.interiorLeatherAudio.getComponent(MeshComponent)!;
		this._doorCardNoAudio = this.doorCardNoAudio.getComponent(MeshComponent)!;
		this._doorCardAudio = this.doorCardAudio.getComponent(MeshComponent)!;
		this._doorCardBase = this.doorCardBase.getComponent(MeshComponent)!;
		this._gloveBox = this.gloveBox.getComponent(MeshComponent)!;
		this._tunnelPocket = this.tunnelPocket.getComponent(MeshComponent)!;
		this._lockableStorage = this.lockableStorage.getComponent(MeshComponent)!;
		this._interiorSideScreen = this.interiorSideScreen.getComponent(MeshComponent)!;
		this._seatBase = this.seatBase.getComponent(MeshComponent)!;
		this._seatBoxStitch = this.seatBoxStitch.getComponent(MeshComponent)!;
		this._seatDiamondStitch = this.seatDiamondStitch.getComponent(MeshComponent)!;
		this._seatHorizontalStitch = this.seatHorizontalStitch.getComponent(MeshComponent)!;
		this._seatBackLeather = this.seatBackLeather.getComponent(MeshComponent)!;
		this._seatBackTextile = this.seatBackTextile.getComponent(MeshComponent)!;
		this._visors = Utilities.getMeshComponentsFromChildren(this.visors);
		this._carpetDescriptions = this.carpetDescriptions.getComponents(CarpetDescription);
		this._carpets = this.carpets.getComponent(MeshComponent)!;
		this._seatHeadrestStitch = this.seatHeadrestStitch.getComponent(MeshComponent)!;
		this._seatHeadrestCustomStitch = this.seatHeadrestCustomStitch.getComponent(MeshComponent)!;
		this._dashFaceDesciptions = this.dashFaceDescriptions.getComponents(DashFaceDescription);
		this._dashFace = this.dashFace.getComponent(MeshComponent)!;
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription);
		this._interiorComponents = this.interiorComponents.getComponent(MeshComponent)!;
		this._centreConsoleDescriptions = this.centreConsoleDescriptions.getComponents(CentreConsoleDescription);
		this._centreConsoleWood = this.centreConsoleWood.getComponent(MeshComponent)!;
		this._centreConsoleLeather = this.centreConsoleLeather.getComponent(MeshComponent)!;
		this._centreConsoleCarPaint = this.centreConsoleCarPaint.getComponent(MeshComponent)!;
		this._centreConsoleMarquetry = this.centreConsoleMarquetry.getComponent(MeshComponent)!;
		this._dashLowerRail = this.dashLowerRail.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Selects gearstick with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectGearStick() {

		if (Configuration.transmission === Configuration.manualTransmissionOption) {
			this.gearStickManual.active = true;
			this.gearStickManualLeather.active = true;
			this.gearStickAuto.active = false;
			this.gearStickAutoLeather.active = false;
		} else {
			this.gearStickManual.active = false;
			this.gearStickManualLeather.active = false;
			this.gearStickAuto.active = true;
			this.gearStickAutoLeather.active = true;
		}
	}
	
	/**
	* Selects centre console style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectCentreConsoleOption(id: string) {

		// get the centre console description for the given ID.
		const centreConsoleDescription = this._centreConsoleDescriptions.find(element => element.consoleId === id);
		if (!centreConsoleDescription) {
			console.error('No centre console description found for id: ', id);
			return;
		}

		// get the material from the description.
		const consoleMaterial = centreConsoleDescription.consoleMaterial?.clone() as Material;
		if (!consoleMaterial) {
			console.error('No centre console material assigned for id: ', id);
			return;
		}

		// get the object from the description.
		const consoleObject = centreConsoleDescription.consoleObject as Object3D;
		if (!consoleObject) {
			console.error('No console object assigned for id: ', id);
			return;
		}

		// get the mesh component of the object.
		const consoleComponent = consoleObject.getComponent(MeshComponent)!;
		if (!consoleComponent) {
			console.error('No console component assigned for id: ', id);
			return;
		}

		// set all consoles to false initially.
		this._centreConsoleWood.active = false;
		this._centreConsoleLeather.active = false;
		this._centreConsoleCarPaint.active = false;
		this._centreConsoleMarquetry.active = false;

		// set the relevant console to active with its material.
		consoleComponent.active = true;

		// update the console material.
		consoleComponent.material = consoleMaterial;

		// get the rail material from the description.
		const railMaterial = centreConsoleDescription.railMaterial?.clone() as Material;
		if (!railMaterial) {
			console.error('No rail material assigned for id: ', id);
			return;
		}

		// update the lower rail material.
		this._dashLowerRail.material = railMaterial;
	}

	/**
	* Selects steering wheel style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSteeringWheelOption(id: string) {

		// enable either the standard or midsummer steering wheel.
		if (id === Configuration.silverSteeringWheelOption) {
			this.steeringWheelStandard.active = true;
			Utilities.setActiveAll(this.steeringWheelMidsummer, false);
		}
		else {
			this.steeringWheelStandard.active = false;
			Utilities.setActiveAll(this.steeringWheelMidsummer, true);
		}
	}

	/**
	* Selects a dashboard face style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectDashboardOption(id: string) {
		const dashboardFace = this._dashFace;
		let material: CarPbrMaterial;

		// check if the dashboad item is the same as body colour.
		if (id === Configuration.dashboardMatchingGlossBodyColourId ||
			id === Configuration.dashboardMatchingMatteBodyColourId) {

			// get a material for the current body paint colour, which handles a bespoke colour having no paint description of its own.
			const bodyPaintMaterial = cloneBodyPaintMaterial(this._paintDescriptions);
			if (!bodyPaintMaterial) {
				return;
			}
			material = bodyPaintMaterial;

			// update the gloss and smoothness depending on whether matte or gloss is selected.
			if (Configuration.dashboardOption === Configuration.dashboardMatchingGlossBodyColourId) {
				material.smoothnessFactor = GLOSS_SMOOTHNESS_VALUE;
				material.clearCoatSmoothness = GLOSS_REFLECTIVE_SPECULAR_VALUE;
			} else {
				material.smoothnessFactor = MATT_SMOOTHNESS_VALUE;
				material.clearCoatSmoothness = MATT_REFLECTIVE_SPECULAR_VALUE;
			}

			// update the material.
			dashboardFace.material = material;

			// keep it, so that a body colour changing afterwards can be written straight onto it - see updateDashboardBodyColour().
			this._bodyMatchingDashboardMaterial = material;

			return;
		}

		// any other dashboard option does not follow the body colour, so nothing is left for updateDashboardBodyColour() to write to.
		this._bodyMatchingDashboardMaterial = null;

		const dashFaceDescription = this._dashFaceDesciptions.find(element => element.dashboardId === id);

		if (!dashFaceDescription) {
			console.error('No dashboard description found for id: ', id);
			return;
		}

		// get the relevant material.
		material = dashFaceDescription.dashboardMaterial?.clone() as CarPbrMaterial;

		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return;
		}

		// apply the material to the dashboard face, unable to fade colour because of switching between gloss and matte.
		dashboardFace.material = material;
	}

	/**
	* Writes the given body colour, as an rgba array of 0–1 floats, onto a body-matching dashboard face, so that it keeps up with a
	* bespoke colour as it is chosen. Does nothing unless a body-matching dashboard option is selected.
	* This overwrites the albedo of the material selectDashboardOption() already built, rather than rebuilding it, so that it is cheap
	* enough to call on every movement of the colour picker - the same trade as PaintConfigurator.selectBespokeBodyPaint() makes.
	* The finish is therefore left exactly as that option set it: only the colour changes.
	*/
	updateDashboardBodyColour(colour: NumberArray) {

		// nothing to do unless the dash face is currently wearing a body-matching material.
		if (!this._bodyMatchingDashboardMaterial) {
			return;
		}

		// overwrite its albedo with the given colour.
		this._bodyMatchingDashboardMaterial.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
	}

	/**
	* Selects a tunnel side style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectCarpetColour(id: string) {
		const carpetDescription = this._carpetDescriptions.find(element => element.carpetId === id);

		if (!carpetDescription) {
			console.error('No carpet description found for id: ', id);
			return;
		}

		// get the relevant material.
		const material = carpetDescription.carpetMaterial as CarpetMaterial;

		// get the relevant mesh components.
		const carpets = this._carpets;
		const tunnelCarpet = this._tunnelCarpet;

		// apply the relevant materials.
		if (material) {
			this._genericFadeColour(carpets, material.albedoColor, 'albedoColor' as FadableKey);
			this._genericFadeColour(tunnelCarpet, material.albedoColor, 'albedoColor' as FadableKey);
		}
	}

	/**
	* Selects a tunnel side style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectTunnelSidesStyle() {
		if (Configuration.tunnelSidesStyle === TunnelSidesOptions.Leather) {
			this.tunnelCarpet.active = false;
			this.tunnelLeather.active = true;
		} else {
			this.tunnelCarpet.active = true;
			this.tunnelLeather.active = false;
		}
	}

	/**
	* Selects a seat pleating style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatPleatingStyle(id: string) {

		// activate the appropriate seat pleating object, and deactivate the rest.
		this.seatHorizontalStitch.active = false;
		this.seatDiamondStitch.active = false;
		this.seatBoxStitch.active = false;
		switch (id as SeatPleatingStyles) {
			case SeatPleatingStyles.Horizontal:
				this.seatHorizontalStitch.active = true;
				break;
			case SeatPleatingStyles.Quilted:
				this.seatDiamondStitch.active = true;
				break;
			case SeatPleatingStyles.Box:
				this.seatBoxStitch.active = true;
				break;
			default:
				console.error('Invalid id passed to "selectSeatPleatingStyle" function ', id);
				break;
		}
	}
	
	/**
	* Selects am embroidery style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectEmbroideryOption(id: string) {

		// toggle the wings embroidery.
		if (id === EmbroideryOptions.Wings) {
			this.seatHeadrestStitch.active = true;
			this.seatHeadrestCustomStitch.active = false;
		}
		// toggle custom embroidery.
		else if (id === EmbroideryOptions.Custom) {
			this.seatHeadrestStitch.active = false;
			this.seatHeadrestCustomStitch.active = true;
		}
		// remove all embroidery.
		else {
			this.seatHeadrestStitch.active = false;
			this.seatHeadrestCustomStitch.active = false;
		}
	}
	
	/**
	* Selects a seat back style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatBackStyle(id: string) {

		// activate the appropriate seat back object, and deactivate the rest.
		this.seatBackLeather.active = false;
		this.seatBackTextile.active = false;
		switch (id as SeatBackStyles) {
			case SeatBackStyles.LeatherWrapped:
				this.seatBackLeather.active = true;
				break;
			case SeatBackStyles.TwoToneTextile:
				this.seatBackTextile.active = true;
				break;
			default:
				console.error('Invalid id passed to "selectSeatBackStyle" function ', id);
				break;
		}
	}
	
	/**
	* Selects a seat stitching style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatStitchingStyle(id: string) {

		// use leather material colour if matched leather selected.
		let colour: NumberArray;
		if (id === Configuration.stitchingMatchingLeatherStyleId) {
			const leatherDescription = this._leatherDescriptions.find(element => element.leatherId === Configuration.leatherStyle);
			colour = (leatherDescription!.leatherMaterial as SeatMaterial).stitchingColor;
		}
		// otherwise get the stitching description for the given ID.
		else {
			const seatStitchingDescription = this._seatStitchingDescriptions.find(element => element.colourId === id);
			if (!seatStitchingDescription) {
				console.error('No seat stitching description found for id: ', id);
				return;
			}
			colour = seatStitchingDescription.colour;
		}

		// smoothly fade between the current stitch colour and the selected colour.
		this._genericFadeColour(this._seatBase, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatBoxStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatDiamondStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatHorizontalStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatHeadrestStitch, colour, 'ambientColor' as FadableKey);
		this._genericFadeColour(this._seatHeadrestCustomStitch, colour, 'ambientColor' as FadableKey);
	}

	/**
	* Selects a leather style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectLeatherStyle(id: string) {

		// get the leather description for the given ID.
		const leatherDescription = this._leatherDescriptions.find(element => element.leatherId === id);
		if (!leatherDescription) {
			console.error('No leather description found for id: ', id);
			return;
		}

		// get the leather type.
		const leatherType = leatherDescription.leatherType;
		const isTwoTone = leatherType === LeatherTypes.TwoTone;

		// get the relevant materials - some depend on the leather types.
		const leatherMaterial = leatherDescription.leatherMaterial as SeatMaterial;
		if (!leatherMaterial) {
			console.error('No leather material assigned for id: ', id);
			return;
		}
		const seatLeatherMaterial = leatherDescription.seatLeatherMaterial as SeatMaterial;
		if (!seatLeatherMaterial) {
			console.error('No seat leather material assigned for id: ', id);
			return;
		}
		const doorLeatherMaterial = leatherDescription.doorLeatherMaterial as SeatMaterial;
		if (!doorLeatherMaterial) {
			console.error('No door leather material assigned for id: ', id);
			return;
		}

		const textileMaterial = leatherDescription.textileMaterial as SeatTextileMaterial;
		const seatTextileOrLeatherMaterial = isTwoTone ? leatherDescription.seatTextileMaterial as SeatMaterial : seatLeatherMaterial;
		const doorTextileOrLeatherMaterial = isTwoTone ? leatherDescription.textileMaterial as SeatMaterial : doorLeatherMaterial;

		// if the leather type has changed (or on first call), set the relevant meshes to the relevant materials.
		if (leatherType !== this._lastLeatherType) {

			// leather material.
			this._interiorLeatherNoAudio.material = leatherMaterial;
			this._interiorLeatherAudio.material = leatherMaterial;
			this._tunnelLeather.material = leatherMaterial;
			this._interiorSideScreen.material = leatherMaterial;
			this._lockableStorage.material = leatherMaterial;
			for (const visor of this._visors) {
				visor.material = leatherMaterial;
			}
			this._gearStickManualLeather.material = leatherMaterial;
			this._gearStickAutoLeather.material = leatherMaterial;
			this._seatBackLeather.material = leatherMaterial;

			// seat leather material.
			this._setOrFadeSeatMaterial(this._seatBase, seatLeatherMaterial, true);
			
			// door leather material.
			this._doorCardNoAudio.material = doorLeatherMaterial;
			this._doorCardAudio.material = doorLeatherMaterial;
			this._gloveBox.material = doorLeatherMaterial;
			this._tunnelPocket.material = doorLeatherMaterial;
			this._centreConsoleLeather.material = doorLeatherMaterial;

			// either the textile or leather materials.
			this._setOrFadeSeatMaterial(this._seatBoxStitch, seatTextileOrLeatherMaterial, true);
			this._setOrFadeSeatMaterial(this._seatDiamondStitch, seatTextileOrLeatherMaterial, true);
			this._setOrFadeSeatMaterial(this._seatHorizontalStitch, seatTextileOrLeatherMaterial, true);
			this._doorCardBase.material = doorTextileOrLeatherMaterial;
			if (isTwoTone) {
				this._seatBackTextile.material = doorTextileOrLeatherMaterial;
			}
		}

		// otherwise, smoothly fade between the current material and the selected material.
		else {

			// leather material.
			this._fadeColour(this._interiorLeatherNoAudio, leatherMaterial);
			this._fadeColour(this._interiorLeatherAudio, leatherMaterial);
			this._fadeColour(this._tunnelLeather, leatherMaterial);
			this._fadeColour(this._interiorSideScreen, leatherMaterial);
			this._fadeColour(this._lockableStorage, leatherMaterial);
			for (const visor of this._visors) {
				this._fadeColour(visor, leatherMaterial);
			}
			this._fadeColour(this._gearStickManualLeather, leatherMaterial);
			this._fadeColour(this._gearStickAutoLeather, leatherMaterial);

			// seat leather material.
			this._setOrFadeSeatMaterial(this._seatBase, seatLeatherMaterial, false);
			this._fadeColour(this._seatBackLeather, seatLeatherMaterial);
			
			// door leather material.
			this._fadeColour(this._doorCardNoAudio, doorLeatherMaterial);
			this._fadeColour(this._doorCardAudio, doorLeatherMaterial);
			this._fadeColour(this._gloveBox, doorLeatherMaterial);
			this._fadeColour(this._tunnelPocket, doorLeatherMaterial);
			this._fadeColour(this._centreConsoleLeather, doorLeatherMaterial);

			// either the textile or leather materials (for seats we only want to fade the seat colour, leaving the stitching colour unchanged).
			this._setOrFadeSeatMaterial(this._seatBoxStitch, seatTextileOrLeatherMaterial, false);
			this._setOrFadeSeatMaterial(this._seatDiamondStitch, seatTextileOrLeatherMaterial, false);
			this._setOrFadeSeatMaterial(this._seatHorizontalStitch, seatTextileOrLeatherMaterial, false);

			this._fadeColour(this._doorCardBase, doorTextileOrLeatherMaterial);
			if (isTwoTone && textileMaterial) {
				this._genericFadeColour(this._seatBackTextile, textileMaterial.albedoColor, 'albedoColor' as FadableKey);
			}
		}

		// update the last type.
		this._lastLeatherType = leatherType;
	}

	/**
	* Gets the current stitching colour for the seat.
	*/
	getCurrentStitchingColour() {
		const seatMaterial = this._seatBoxStitch.material as SeatMaterial;
		return seatMaterial.stitchingColor;
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Provides special handling when setting or fading the seat materials, as we only want to set the seat colour, not the stitching colours,
	* unless they are set to match the leather colour.
	*/
	private _setOrFadeSeatMaterial(mesh: MeshComponent, seatLeatherMaterial: SeatMaterial, shouldSet: boolean) {

		// get the stitching colours from material currently assigned to the given mesh.
		const currentMaterial = mesh.material as SeatMaterial;
		const stitchingColour = currentMaterial.stitchingColor;

		// clone the given material.
		const newMaterial = seatLeatherMaterial.clone() as SeatMaterial;

		// set the stitching colour, unless it should match the leather colour.
		if (Configuration.seatStitchingOption !== Configuration.stitchingMatchingLeatherStyleId) {
			newMaterial.stitchingColor = stitchingColour;
		}

		// set or fade to the new material.
		if (shouldSet) {
			mesh.material = newMaterial;
		}
		else {
			this._fadeColour(mesh, newMaterial, false);
		}
	}

	/**
	* Smoothly fades the colour of any material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _genericFadeColour(
		mesh: MeshComponent,
		targetColour: NumberArray,
		targetProperty: FadableKey
	) {
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		const material = mesh.material.clone() as FadableMaterial;
		mesh.material = material;

		const prop = targetProperty as string;

		const colourTween = new Tween({
			[prop]: material[targetProperty],
		});
		colourTween
			.to(
				{
					[prop]: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				(material as Record<FadableKey, NumberArray>)[targetProperty] = c[prop];
			})
			.easing(Easing.Quadratic.InOut);

		this._tweenManager.start(colourTween);
	}

	/**
	* Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	* using a tween animation to change the relevant material properties.
	*
	* Note that on the leather materials, the stitching colour property actually defines the main colour. We set all properties here for consistency.
	* The exception is the seat leather and seat textile materials, which only need the seat colour to be set, and support separate piping and stitching colours.
	*/
	private _fadeColour(mesh: MeshComponent, targetMaterial: SeatMaterial, onlySeatColour?: boolean) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as SeatMaterial;
		mesh.material = material;

		// tween the relevant material properties to change the colour.
		const colourTween = new Tween({
			seatColor: material.seatColor,
			pipeColor: material.pipeColor,
			stitchingColor: material.stitchingColor
		});
		colourTween
			.to(
				{
					seatColor: targetMaterial.seatColor,
					pipeColor: targetMaterial.pipeColor,
					stitchingColor: targetMaterial.stitchingColor
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.seatColor = c.seatColor;

				// optionally tween all properties.
				if (!onlySeatColour) {
					material.pipeColor = c.pipeColor;
					material.stitchingColor = c.stitchingColor;
				}
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}

